/**
 * GovernanceEnforcementLog Component
 * AIDLC Administrative Backend
 *
 * Two sections:
 *   1. Enforcement Log — recent governance evaluations (table)
 *   2. Pending Post-Checks — cards with Complete/Waive actions
 *
 * Real-time updates via WebSocket events:
 *   - governance:evaluation
 *   - governance:post-check-new
 *   - governance:post-check-complete
 *   - governance:escalation
 */

import { useState, useEffect, useCallback } from 'react';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import {
  getEnforcementLog,
  getPostChecks,
  completePostCheck,
  type GovernanceAuditEntry,
  type GovernancePostCheck,
} from '../../services/governanceEnforcementService';
import { useBridge } from '../layout/MainLayout';

const verdictColors: Record<string, 'success' | 'danger' | 'warning'> = {
  allowed: 'success',
  blocked: 'danger',
  'approval-needed': 'warning',
};

const checkTypeLabels: Record<string, string> = {
  'require-review': 'Code Review',
  'require-test': 'QA Testing',
  'quality-gate': 'Quality Gate',
  'step-sequence': 'Workflow Steps',
  'escalation-scan': 'Escalation',
};

function timeAgo(ts: string): string {
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function GovernanceEnforcementLog() {
  const [auditEntries, setAuditEntries] = useState<GovernanceAuditEntry[]>([]);
  const [postChecks, setPostChecks] = useState<GovernancePostCheck[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { socket } = useBridge();

  const loadData = useCallback(async () => {
    try {
      const [logResult, checks] = await Promise.all([
        getEnforcementLog(50),
        getPostChecks(),
      ]);
      setAuditEntries(logResult.entries);
      setPostChecks(checks);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load enforcement data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleEvaluation = (data: { audit: GovernanceAuditEntry }) => {
      setAuditEntries(prev => [data.audit, ...prev].slice(0, 50));
    };

    const handleNewPostCheck = (check: GovernancePostCheck) => {
      setPostChecks(prev => [check, ...prev]);
    };

    const handlePostCheckComplete = (check: GovernancePostCheck) => {
      setPostChecks(prev => prev.map(c => c.id === check.id ? check : c));
    };

    const handleEscalation = () => {
      // Refresh to get latest data
      loadData();
    };

    socket.on('governance:evaluation', handleEvaluation);
    socket.on('governance:post-check-new', handleNewPostCheck);
    socket.on('governance:post-check-complete', handlePostCheckComplete);
    socket.on('governance:escalation', handleEscalation);

    return () => {
      socket.off('governance:evaluation', handleEvaluation);
      socket.off('governance:post-check-new', handleNewPostCheck);
      socket.off('governance:post-check-complete', handlePostCheckComplete);
      socket.off('governance:escalation', handleEscalation);
    };
  }, [socket, loadData]);

  const handleAction = async (checkId: string, action: 'completed' | 'waived') => {
    setActionLoading(checkId);
    try {
      const updated = await completePostCheck(checkId, action);
      setPostChecks(prev => prev.map(c => c.id === updated.id ? updated : c));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update post-check');
    } finally {
      setActionLoading(null);
    }
  };

  if (isLoading) return <LoadingSpinner message="Loading enforcement data..." />;

  const pendingChecks = postChecks.filter(c => c.status === 'pending');
  const recentChecks = postChecks.filter(c => c.status !== 'pending').slice(0, 20);

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-700 text-xs">{error}</p>
          <button onClick={() => setError(null)} className="text-red-600 text-xs underline mt-1">Dismiss</button>
        </div>
      )}

      {/* Pending Post-Checks */}
      <div>
        <h3 className="text-sm font-semibold text-ldaja-black mb-3 flex items-center gap-2">
          Pending Post-Checks
          {pendingChecks.length > 0 && (
            <span className="bg-amber-100 text-amber-700 text-[10px] font-medium px-1.5 py-0.5 rounded-full">
              {pendingChecks.length}
            </span>
          )}
        </h3>

        {pendingChecks.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-xs text-gray-500">No pending post-checks. All clear.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {pendingChecks.map(check => (
              <div key={check.id} className="bg-white border border-amber-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <Badge variant="warning">
                    {checkTypeLabels[check.type] || check.type}
                  </Badge>
                  <span className="text-[10px] text-gray-400">{timeAgo(check.createdAt)}</span>
                </div>
                <p className="text-xs text-gray-700 mb-2">{check.description}</p>
                <div className="text-[10px] text-gray-400 mb-3">
                  <span>From: <span className="font-medium text-gray-600">{check.originAgent}</span></span>
                  <span className="mx-2">|</span>
                  <span>Task: {check.originTask}</span>
                  {check.assignedAgent && (
                    <>
                      <span className="mx-2">|</span>
                      <span>Assigned: <span className="font-medium text-gray-600">{check.assignedAgent}</span></span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleAction(check.id, 'completed')}
                    isLoading={actionLoading === check.id}
                    disabled={!!actionLoading}
                  >
                    Complete
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleAction(check.id, 'waived')}
                    disabled={!!actionLoading}
                  >
                    Waive
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recently Resolved Post-Checks */}
      {recentChecks.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-ldaja-black mb-3">Recently Resolved</h3>
          <div className="space-y-2">
            {recentChecks.map(check => (
              <div key={check.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant={check.status === 'completed' ? 'success' : 'neutral'}>
                    {check.status === 'completed' ? 'Done' : 'Waived'}
                  </Badge>
                  <span className="text-xs text-gray-600">{check.description}</span>
                </div>
                <span className="text-[10px] text-gray-400">
                  {check.completedBy} {check.completedAt ? timeAgo(check.completedAt) : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Enforcement Log */}
      <div>
        <h3 className="text-sm font-semibold text-ldaja-black mb-3">Enforcement Log</h3>

        {auditEntries.length === 0 ? (
          <EmptyState
            title="No enforcement records yet"
            description="Governance evaluations will appear here as agents are dispatched."
          />
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-3 py-2 font-medium text-gray-500">Time</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-500">Agent</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-500">Type</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-500">Subject</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-500">Rules</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-500">Verdict</th>
                </tr>
              </thead>
              <tbody>
                {auditEntries.map(entry => (
                  <tr key={entry.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{timeAgo(entry.timestamp)}</td>
                    <td className="px-3 py-2 font-medium text-gray-700">{entry.agentId}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        entry.taskType === 'go-do-it'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {entry.taskType === 'go-do-it' ? 'Go Do It' : 'Chat'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600 max-w-[250px] truncate">{entry.subject}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="text-gray-500">{entry.rulesMatched}/{entry.rulesEvaluated}</span>
                      {entry.postCheckCount > 0 && (
                        <span className="ml-1 text-amber-500" title={`${entry.postCheckCount} post-checks`}>
                          +{entry.postCheckCount}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Badge variant={verdictColors[entry.verdict] || 'info'}>
                        {entry.verdict}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
